import { useState, useEffect } from 'react';
import { Cookie, X, ChevronLeft } from 'lucide-react';
import { useTranslation } from '../../i18n/LanguageContext';
import './CookieBanner.css';

const CookieBanner = ({ darkMode = false }) => {
  const { t } = useTranslation();
  const [cookieSettings, setCookieSettings] = useState({
    essential: true, analytics: false, marketing: false, preferences: false
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isDetailView, setIsDetailView] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('cookieConsent');
    if (saved) { setCookieSettings(JSON.parse(saved)); setHasInteracted(true); }
    else setIsOpen(true);
  }, []);

  const save = (settings) => { localStorage.setItem('cookieConsent', JSON.stringify(settings)); setHasInteracted(true); };
  const acceptAll = () => { const s = { essential: true, analytics: true, marketing: true, preferences: true }; setCookieSettings(s); save(s); setIsOpen(false); };
  const acceptEssential = () => { const s = { essential: true, analytics: false, marketing: false, preferences: false }; setCookieSettings(s); save(s); setIsOpen(false); };
  const saveCustom = () => { save(cookieSettings); setIsOpen(false); };
  const toggle = (cat) => { if (cat === 'essential') return; setCookieSettings(p => ({ ...p, [cat]: !p[cat] })); };

  const bg          = darkMode ? 'rgba(12,12,20,0.97)' : 'rgba(255,255,255,0.97)';
  const border      = darkMode ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.07)';
  const textPrimary = darkMode ? '#ffffff' : '#0f0f1a';
  const textMuted   = darkMode ? 'rgba(255,255,255,0.55)' : 'rgba(15,15,26,0.5)';
  const optionBg    = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)';
  const optionBorder= darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)';
  const accent      = '#6366f1';
  const accent2     = '#8b5cf6';

  const btnPrimary = {
    padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer',
    background: `linear-gradient(135deg, ${accent}, ${accent2})`,
    color: '#fff', fontWeight: 700, fontSize: 13,
    boxShadow: `0 4px 14px ${accent}30`, transition: 'transform 0.2s',
  };
  const btnSecondary = {
    padding: '10px 16px', borderRadius: 10, border: `1px solid ${border}`,
    cursor: 'pointer', background: 'transparent', color: textMuted,
    fontWeight: 600, fontSize: 13, transition: 'border-color 0.2s, color 0.2s',
  };

  if (!isOpen && hasInteracted) {
    return (
      <button onClick={() => { setIsOpen(true); setIsDetailView(true); }}
        style={{
          position: 'fixed', bottom: 20, left: 20, width: 40, height: 40, borderRadius: '50%',
          background: bg, border: `1px solid ${border}`, backdropFilter: 'blur(16px)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', cursor: 'pointer', zIndex: 999, color: accent,
          transition: 'transform 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        aria-label="Cookie-Einstellungen"
      >
        <Cookie size={16} />
      </button>
    );
  }

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
      width: 'min(520px, calc(100vw - 24px))', zIndex: 9999,
      animation: 'cookie-slide-up 0.35s cubic-bezier(0.16,1,0.3,1)',
    }}>
      <div style={{
        background: bg, backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
        border: `1px solid ${border}`, borderRadius: 16,
        boxShadow: '0 8px 40px rgba(0,0,0,0.22)', overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{ padding: '16px 18px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: accent + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color: accent }}>
              <Cookie size={14} />
            </div>
            <span style={{ fontWeight: 800, fontSize: 14, color: textPrimary }}>{t('cookie.title')}</span>
          </div>
          {hasInteracted && (
            <button onClick={() => setIsOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: textMuted, display: 'flex', padding: 4, borderRadius: 6, transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = textPrimary}
              onMouseLeave={e => e.currentTarget.style.color = textMuted}
            ><X size={15} /></button>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: '12px 18px' }}>
          {!isDetailView ? (
            <>
              <p style={{ fontSize: 13, color: textMuted, lineHeight: 1.65, margin: '0 0 14px' }}>{t('cookie.description')}</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button style={btnSecondary} onClick={acceptEssential}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = accent + '50'; e.currentTarget.style.color = textPrimary; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = border; e.currentTarget.style.color = textMuted; }}
                >{t('cookie.essential')}</button>
                <button style={btnSecondary} onClick={() => setIsDetailView(true)}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = accent + '50'; e.currentTarget.style.color = textPrimary; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = border; e.currentTarget.style.color = textMuted; }}
                >{t('cookie.customize')}</button>
                <button style={{ ...btnPrimary, marginLeft: 'auto' }} onClick={acceptAll}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                >{t('cookie.acceptAll')}</button>
              </div>
            </>
          ) : (
            <>
              <p style={{ fontSize: 13, color: textMuted, lineHeight: 1.65, margin: '0 0 12px' }}>{t('cookie.customizeDescription')}</p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                {[
                  { id: 'essential',   title: t('cookie.essentialTitle'),   desc: t('cookie.essentialDesc'),   disabled: true },
                  { id: 'analytics',   title: t('cookie.analyticsTitle'),   desc: t('cookie.analyticsDesc'),   disabled: false },
                  { id: 'marketing',   title: t('cookie.marketingTitle'),   desc: t('cookie.marketingDesc'),   disabled: false },
                  { id: 'preferences', title: t('cookie.preferencesTitle'), desc: t('cookie.preferencesDesc'), disabled: false },
                ].map(({ id, title, desc, disabled }) => (
                  <div key={id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: optionBg, border: `1px solid ${optionBorder}`, borderRadius: 10, gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: textPrimary, marginBottom: 2 }}>{title}</div>
                      <div style={{ fontSize: 11, color: textMuted, lineHeight: 1.5 }}>{desc}</div>
                    </div>
                    <div className={`cookie-toggle${disabled ? ' cookie-toggle--disabled' : ''}`} style={{ flexShrink: 0 }}>
                      <input type="checkbox" id={`cookie-${id}`} checked={cookieSettings[id]} onChange={() => toggle(id)} readOnly={disabled} />
                      <label htmlFor={`cookie-${id}`} />
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button style={{ ...btnSecondary, display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => setIsDetailView(false)}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = accent + '50'; e.currentTarget.style.color = textPrimary; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = border; e.currentTarget.style.color = textMuted; }}
                ><ChevronLeft size={13} />{t('cookie.back')}</button>
                <button style={btnPrimary} onClick={saveCustom}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                >{t('cookie.saveSettings')}</button>
              </div>
            </>
          )}
        </div>

        {/* Footer info */}
        <div style={{ padding: '10px 18px', borderTop: `1px solid ${border}`, background: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
          <p style={{ margin: 0, fontSize: 11, color: textMuted }}>
            {t('cookie.privacyInfo')}{' '}
            <a href="/datenschutz" onClick={() => setIsOpen(false)} style={{ color: accent, textDecoration: 'none' }}>{t('cookie.privacyLink')}</a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
